/**
 * Stripe Payment Service for Portal
 * Handles credit top-up payments via Stripe
 */

import { getRuntimeConfig } from '../config/runtime';

function getApiUrl(): string {
  const config = getRuntimeConfig();
  return config?.VITE_STRIPE_API_URL || config?.VITE_C1_API_URL || '';
}

// ─── Types ──────────────────────────────────────────────────────────────────

export type CreditPackageId = 'CREDITS_STARTER' | 'CREDITS_GROWTH' | 'CREDITS_ENTERPRISE' | 'CREDITS_OFFER';

export function mapPlanToPackageId(plan: string): CreditPackageId {
  const mapping: Record<string, CreditPackageId> = {
    STARTER: 'CREDITS_STARTER',
    GROWTH: 'CREDITS_GROWTH',
    ENTERPRISE: 'CREDITS_ENTERPRISE',
    OFFER: 'CREDITS_OFFER',
  };
  return mapping[plan] ?? 'CREDITS_STARTER';
}

export interface CreatePaymentIntentRequest {
  packageId?: CreditPackageId;
  amount?: number;
  customerEmail: string;
  customerName: string;
  customerId?: string;
  idempotencyKey?: string;
  metadata?: Record<string, string>;
}

export interface CreatePaymentIntentResponse {
  clientSecret?: string;
  paymentIntentId: string;
  amount?: number;
  currency?: string;
  invoiceId?: string;
  stripeCustomerId?: string;
  reused?: boolean;
  alreadyPaid?: boolean;
  status?: string;
  message?: string;
}

export interface ConfirmPaymentResponse {
  success: boolean;
  status: string;
  credits: number;
  receiptUrl?: string;
}

export interface GetConfigResponse {
  publishableKey: string;
}

// ─── API Functions ──────────────────────────────────────────────────────────

export async function getStripeConfig(): Promise<GetConfigResponse> {
  const response = await fetch(`${getApiUrl()}/stripe/config`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response.ok) throw new Error('Failed to fetch Stripe config');
  return response.json();
}

export async function createPaymentIntent(
  request: CreatePaymentIntentRequest
): Promise<CreatePaymentIntentResponse> {
  const response = await fetch(`${getApiUrl()}/stripe/create-payment-intent`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });
  if (!response.ok) {
    const text = await response.text();
    let msg = 'Failed to create payment intent';
    try { msg = JSON.parse(text).message || msg; } catch { msg = text || msg; }
    throw new Error(msg);
  }
  return response.json();
}

export async function confirmPayment(paymentIntentId: string): Promise<ConfirmPaymentResponse> {
  const response = await fetch(`${getApiUrl()}/stripe/confirm-payment`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ paymentIntentId }),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to confirm payment');
  }
  return response.json();
}
